import { useMutation } from '@tanstack/react-query'
import { Loader2, LucideHeart } from 'lucide-react'
import { useState } from 'react'
import type { MapRef } from 'react-map-gl'
import { aUser, useAtom } from '~/atoms'
import { ControlTooltip } from '~/components/common'
import { Button, toast } from '~/components/ui'
import type { User, UserMap } from '~/models'

type MapHomeProps = {
  userId: User['_id']
  map: UserMap['initialViewState']
}

async function setMapHome({ userId, map }: MapHomeProps) {
  const response = await fetch('/api/set-map-home', {
    body: JSON.stringify({ userId, map }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error('Failed to set map home')
  }

  return response.json()
}

export default function SetMapHome({ map }: { map: MapRef }) {
  const [user, setUser] = useAtom(aUser)
  const [viewState, setViewState] = useState<
    UserMap['initialViewState'] | undefined
  >()

  const mutation = useMutation({
    mutationFn: setMapHome,
    onSuccess: () => {
      toast({
        duration: 2000,
        variant: 'success',
        title: 'Success',
        description: 'Set current view as map home',
      })

      if (user && viewState) {
        setUser({
          ...user,
          options: {
            ...(user.options || {}),
            map: {
              ...user.options?.map,
              initialViewState: viewState,
            },
          },
        })
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Error Setting Map Home',
        description:
          'There was a problem saving the current view as map home. Please try again or contact support if the problem persists.',
      })
    },
  })

  if (!user) {
    return null
  }

  return (
    <ControlTooltip
      side="right"
      sideOffset={10}
      text="Set current view as map home">
      <Button
        id="map-set-home"
        disabled={mutation.isPending}
        variant="map"
        size="map"
        className="mb-2 h-7 w-7 bg-background shadow-none"
        onClick={() => {
          const { lat, lng } = map.getCenter()
          const zoom = map.getZoom()

          const viewState: UserMap['initialViewState'] = {
            latitude: lat,
            longitude: lng,
            zoom,
          }

          setViewState(viewState)

          mutation.mutate({
            userId: user._id,
            map: viewState,
          })
        }}>
        {mutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <LucideHeart className="h-4 w-4" />
        )}
      </Button>
    </ControlTooltip>
  )
}
